import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, FileText, CheckSquare, AlertTriangle,
  Loader2, RefreshCw, Calendar, ChevronRight, Inbox
} from 'lucide-react';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { format, parseISO, isToday, isYesterday } from 'date-fns';
import { nb, enUS } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../../context/LanguageContext';
import { activityApi } from '../../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ACTIVITY FEED VIEW - Minimalist design
 */
const ActivityFeedViewMinimal = () => {
  const navigate = useNavigate();
  const { language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [grouped, setGrouped] = useState<Record<string, any[]>>({});
  const [summary, setSummary] = useState<{ emails?: number; documents?: number; tasks?: number; deadlines?: number }>({});
  const [filter, setFilter] = useState('all');
  const [days, setDays] = useState(7);

  // Translations
  const texts = {
    title: language === 'en' ? 'Activity Overview' : 'Aktivitetsoversikt',
    subtitle: language === 'en' ? 'Everything happening across your cases' : 'Alt som skjer på tvers av sakene dine',
    emails: language === 'en' ? 'Emails' : 'E-poster',
    documents: language === 'en' ? 'Documents' : 'Dokumenter',
    tasks: language === 'en' ? 'Tasks' : 'Oppgaver',
    deadlines: language === 'en' ? 'Deadlines' : 'Frister',
    all: language === 'en' ? 'All' : 'Alt',
    email: language === 'en' ? 'Email' : 'E-post',
    document: language === 'en' ? 'Document' : 'Dokument',
    noActivities: language === 'en' ? 'No activities' : 'Ingen aktiviteter',
    quietNow: language === 'en' ? "It's quiet right now" : 'Det er rolig akkurat nå',
    today: language === 'en' ? 'Today' : 'I dag',
    yesterday: language === 'en' ? 'Yesterday' : 'I går',
    thisWeek: language === 'en' ? 'This week' : 'Denne uken',
    older: language === 'en' ? 'Older' : 'Eldre',
    last24Hours: language === 'en' ? 'Last 24 hours' : 'Siste 24 timer',
    last7Days: language === 'en' ? 'Last 7 days' : 'Siste 7 dager',
    last14Days: language === 'en' ? 'Last 14 days' : 'Siste 14 dager',
    last30Days: language === 'en' ? 'Last 30 days' : 'Siste 30 dager'
  };

  const fetchActivities = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const token = auth.getToken();
      const params = new URLSearchParams();
      params.append('days', String(days));
      params.append('limit', '50');
      if (filter !== 'all') params.append('activity_type', filter);
      
      const response = await axios.get(`${API}/activity-feed?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setActivities(response.data.activities || []);
      setGrouped(response.data.grouped || {});
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching activity feed:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter, days]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = parseISO(timestamp);
      const locale = language === 'en' ? enUS : nb;
      if (isToday(date)) return format(date, 'HH:mm', { locale });
      if (isYesterday(date)) return `${texts.yesterday} ${format(date, 'HH:mm', { locale })}`;
      return format(date, 'd. MMM HH:mm', { locale });
    } catch (e) {
      return '';
    }
  };

  const handleCaseClick = (caseId) => {
    if (caseId) navigate(`/lexy/ai-workspace?case=${caseId}`);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'email': return Mail;
      case 'document': return FileText;
      case 'task': return CheckSquare;
      case 'deadline': return AlertTriangle;
      default: return FileText;
    }
  };

  const renderActivityItem = (activity, index) => {
    const Icon = getActivityIcon(activity.type);
    
    return (
      <motion.div
        key={activity.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: index * 0.02 }}
        onClick={() => handleCaseClick(activity.case_id)}
        className="group flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-all"
      >
        {/* Icon */}
        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-gray-500" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
          <p className="text-xs text-gray-500 truncate">{activity.description}</p>
        </div>
        
        {/* Case tag */}
        {activity.case_title && (
          <span className="text-xs text-gray-400 truncate max-w-[150px]">
            {activity.case_title}
          </span>
        )}
        
        {/* Time */}
        <span className="text-xs text-gray-400 w-20 text-right flex-shrink-0">
          {formatTime(activity.timestamp)}
        </span>
        
        {/* Arrow */}
        <ChevronRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      </motion.div>
    );
  };

  const renderSection = (title, items) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 px-4">
          {title} ({items.length})
        </h3>
        <div className="space-y-1">
          {items.map((activity, idx) => renderActivityItem(activity, idx))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-8 py-12">
        
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{texts.title}</h1>
            <button
              onClick={() => fetchActivities(true)}
              disabled={refreshing}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <p className="text-gray-500 text-sm">{texts.subtitle}</p>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-8 mb-8 pb-8 border-b border-gray-100">
          <div>
            <div className="text-3xl font-semibold text-gray-900">{summary.emails || 0}</div>
            <div className="text-sm text-gray-500">{texts.emails}</div>
          </div>
          <div className="w-px h-10 bg-gray-100" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{summary.documents || 0}</div>
            <div className="text-sm text-gray-500">{texts.documents}</div>
          </div>
          <div className="w-px h-10 bg-gray-100" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{summary.tasks || 0}</div>
            <div className="text-sm text-gray-500">{texts.tasks}</div>
          </div>
          <div className="w-px h-10 bg-gray-100" />
          <div>
            <div className="text-3xl font-semibold text-red-600">{summary.deadlines || 0}</div>
            <div className="text-sm text-gray-500">{texts.deadlines}</div>
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
            {[
              { id: 'all', label: texts.all },
              { id: 'email', label: texts.email },
              { id: 'document', label: texts.documents },
              { id: 'task', label: texts.tasks },
              { id: 'deadline', label: texts.deadlines }
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setFilter(opt.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  filter === opt.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="text-sm border-0 bg-transparent text-gray-600 focus:ring-0 cursor-pointer"
            >
              <option value={1}>{texts.last24Hours}</option>
              <option value={7}>{texts.last7Days}</option>
              <option value={14}>{texts.last14Days}</option>
              <option value={30}>{texts.last30Days}</option>
            </select>
          </div>
        </div>

        {/* Activity list */}
        {activities.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">{texts.noActivities}</h3>
            <p className="text-gray-500 text-sm">{texts.quietNow}</p>
          </div>
        ) : (
          <AnimatePresence>
            {renderSection(texts.today, grouped.today)}
            {renderSection(texts.yesterday, grouped.yesterday)}
            {renderSection(texts.thisWeek, grouped.this_week)}
            {renderSection(texts.older, grouped.older)}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default ActivityFeedViewMinimal;
